/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import { batch, useDispatch, useSelector } from 'react-redux';
import ResizeObserver from 'react-resize-observer';

import HeaderText from '../../shared/_TBComponents/HeaderText';
import CompletionOverview from './components/presentational/CompletionOverview';
import GroupAnalysis from './components/presentational/GroupAnalysis';
import {
  setHabitTrackingView,
  setOnboardingModalOpen,
} from './ducks/habitTrackingDashboardActions';
import { setModalOpen, setViewedFeatures } from '../sports-science/ducks/sportsScienceActions';
import {
  dashboardTracker,
  onboardingTracker,
} from '../../shared/utils/amplitudeHelper';
import {
  fetchHabitsForAccount,
  fetchReportableGroups,
  fetchAthletes,
  fetchCategoriesForAccount,
} from '../habit-tracking/ducks/habitTrackingActions';
import HabitTracking from '../habit-tracking/HabitTracking';
import OnboardingModal from '../../shared/_TBComponents/OnboardingModal';
import {
  FEATURES,
  hasViewedFeature,
  createViewedFeaturePayload,
} from '../../shared/utils/onboardingStatus';
import { TransitionGlobal } from '../../shared/GlobalStyles';
import Icon from '../../shared/_TBComponents/Icon';
import { getModuleInfoByTitle } from '../sports-science/components/presentational/packages';

const Container = styled('div')`
  width: 100%;
`;

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 1450px;
`;

const DashboardHeader = styled('div')`
  display: flex;
  width: 100%;
  background-color: #ffffff;
  padding-top: 30px;
  padding-left: 20px;
  justify-content: space-between;

  h1 {
    padding-bottom: 8px;
  }
`;

const MenuContainer = styled('div')`
  display: flex;
`;

const MenuItem = styled('button')`
  border: none;
  margin-left: 10px;
  margin-right: 12px;
  font-family: 'Nunito Sans';
  font-weight: 400;
  font-size: 16px;
  border-bottom: ${(props) => (props.isActive ? '3px solid #1787FB!important' : '3px solid white')};
  color: ${(props) => (!props.isActive ? '#9E9E9E' : '#444444')};
`;

const InfoWrapper = styled('div')`
  display: flex;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  height: 20px;
  width: 20px;
  border: 2px solid #9e9e9e;
  margin-left: 10px;
  margin-bottom: 8px;
  cursor: ${(props) => (props.disabled ? 'cursor' : 'pointer')};
  :hover {
    opacity: ${(props) => (props.disabled ? '1' : '.7')};
  }
  transition: ${TransitionGlobal};
  @media only screen and (max-width: 601px) {
    display: none;
  }
`;

const HeaderContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const HeaderAndSubheader = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const SubheaderText = styled('h2')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  margin-left: 6px;
  margin-bottom: 6px;
  color: #9e9e9e;
`;

const HabitTrackingDashboard = () => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const habitsForAccount = useSelector(
    (state) => state.habitTracking.data.habits,
  );
  const accountCode = currentUser?.accountCode;
  const categoriesForAccount = useSelector(
    (state) => state.habitTracking.data.categories,
  );
  const habitTrackingView = useSelector(
    (state) => state.habitTrackingDashboard.ui.habitTrackingView,
  );
  const isOnboardingModalOpen = useSelector(
    (state) => state.habitTrackingDashboard.ui.isOnboardingModalOpen,
  );
  const viewedFeatures = useSelector((state) => state.sportsScience.data.viewedFeatures);
  const viewedFeaturesLoading = useSelector(
    (state) => state.sportsScience.ui.viewedFeaturesLoading,
  );
  const isFeatureViewed = hasViewedFeature(viewedFeatures, FEATURES.HABIT_TRACKER);
  const isSideBarOpen = useSelector((state) => state.sportsScience.data.isSideBarOpen);

  const dispatch = useDispatch();
  const [containerWidth, setContainerWidth] = useState(0);

  const moduleInfo = getModuleInfoByTitle('Habit Tracker');
  const { content } = moduleInfo;

  useEffect(() => {
    dispatch(setOnboardingModalOpen(false));
    if (!viewedFeaturesLoading && !isFeatureViewed && (containerWidth > 714 || !isSideBarOpen)) {
      dispatch(setOnboardingModalOpen(true));
      // Amplitude tracking
      onboardingTracker(
        'Modal Opened',
        'Consistency Coach',
        'Onboarding modal opened on user\'s first view of dashboard',
      );
    }
  }, [isFeatureViewed, viewedFeaturesLoading, dispatch, containerWidth, isSideBarOpen]);

  // Function to handle menu clicks and switch views
  const handleMenuClick = (view) => {
    if (view !== habitTrackingView) {
      if (view === 'management') {
        dispatch(fetchHabitsForAccount(accountCode));
        dispatch(fetchReportableGroups(accountCode));
        dispatch(fetchAthletes(accountCode));

        // Fetch habits and categories if not already loaded
        if (!habitsForAccount?.length) {
          dispatch(fetchHabitsForAccount(accountCode));
        }
        if (!categoriesForAccount?.length) {
          dispatch(fetchCategoriesForAccount(accountCode));
        }
      }

      // Set the selected habit tracking view
      dispatch(setHabitTrackingView(view));

      // Amplitude tracking
      dashboardTracker(
        'Consistency Coach',
        view === 'group'
          ? 'Group Analysis'
          : view === 'management'
            ? 'Management Overview'
            : 'Performance Overview',
        'Menu item clicked',
      );
    }
  };

  const handleClose = (createHabit = false) => {
    const viewedFeaturePayload = createViewedFeaturePayload(
      accountCode,
      currentUser.id,
      FEATURES.HABIT_TRACKER,
    );
    batch(() => {
      dispatch(setOnboardingModalOpen(false));
      dispatch(setViewedFeatures(viewedFeaturePayload));
    });
    if (createHabit) {
      dispatch(setModalOpen(true, 'habitTracking-create-habit'));
      // Amplitude tracking
      dashboardTracker(
        'Consistency Coach',
        'Create Habit Clicked',
        'Create new habit button clicked',
      );
    }
  };

  const handleOpenOnboarding = () => {
    dispatch(setOnboardingModalOpen(true));
  };

  return (
    <Container>
      <ResizeObserver
        onResize={(rect) => {
          setContainerWidth(rect?.width);
        }}
      />
      <Helmet>
        <title>Habit Tracker | TeamBuildr</title>
      </Helmet>
      <PageWrapper>
        <DashboardHeader>
          <HeaderContainer>
            <HeaderAndSubheader>
              <HeaderText fontWeight='300' letterSpacing='normal'>
                Habit Tracker
              </HeaderText>
              {containerWidth > 904 && (
                <SubheaderText>(Formerly Consistency Coach)</SubheaderText>
              )}
            </HeaderAndSubheader>

            <InfoWrapper onClick={handleOpenOnboarding}>
              <Icon
                icon='info'
                size='12px'
                styledProps={{
                  display: 'flex',
                }}
              />
            </InfoWrapper>
          </HeaderContainer>
          <MenuContainer>
            <MenuItem
              isActive={habitTrackingView === 'management'}
              onClick={() => handleMenuClick('management')}
            >
              Habits Management
            </MenuItem>

            <MenuItem
              isActive={habitTrackingView === 'performance'}
              onClick={() => handleMenuClick('performance')}
            >
              Completion Overview
            </MenuItem>

            <MenuItem
              isActive={habitTrackingView === 'group'}
              onClick={() => handleMenuClick('group')}
            >
              Group Analysis
            </MenuItem>
          </MenuContainer>
        </DashboardHeader>

        {habitTrackingView === 'management' && <HabitTracking />}
        {habitTrackingView === 'performance' && <CompletionOverview />}
        {habitTrackingView === 'group' && <GroupAnalysis />}
      </PageWrapper>
      <OnboardingModal
        isActive={isOnboardingModalOpen}
        content={content}
        maxHeight={null}
        minHeight={null}
        dashName='Habit Tracker'
        onClose={handleClose}
        onPrimaryAction={() => handleClose(true)}
        onSecondaryAction={handleClose}
        badgeText='GET STARTED'
      />
    </Container>
  );
};

export default HabitTrackingDashboard;
